import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Form, Input, Label, Spinner } from 'reactstrap';
import makeAnimated from 'react-select/animated';
import Select from 'react-select';
import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';
import useAxios from '../../hooks/useAxios';
import { showToast } from '../../components/toast/Toast';
import { AuthContext } from '../../context/AuthContext';
import FormTimeOffManualAdmin from './FormTimeOffManualAdmin';
import SelectOption from '../../components/selectOption/SelectOption';

const adminOptionsAllowedRole = '4012';

const FormTimeOff = ({ setPopup, refetch }) => {
  const { auth } = useContext(AuthContext);
  const [isFromManual, setIsFromManual] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [typeOptions, setTypeOptions] = useState([]);
  const [showRequestType, setShowRequestType] = useState(false);
  const animatedComponents = makeAnimated();
  const [requester, setRequester] = useState(null);
  const [approver, setApprover] = useState(null);
  const [requesterDelegate, setRequesterDelegate] = useState(null);
  const [objectData, setObjectData] = useState({
    date_start: '',
    date_end: '',
    attachment: null,
    reason: '',
  });

  const compareDate = (date1, date2) => {
    const d1 = new Date(date1);
    const d2 = new Date(date2);

    if (d1 > d2) {
      showToast('Tanggal Invalid', 'error');
      return false;
    }

    return true;
  };

  const api = useAxios();

  const { isLoading: isLoadingTypes, data } = useQuery({
    queryKey: ['time-off-master'],
    queryFn: () =>
      api.get(`/time-off-master`).then((res) => {
        return res.data.data;
      }),
  });

  useEffect(() => {
    if (typeOptions.length === 0) {
      data?.map((type) => {
        return setTypeOptions((prev) => [
          ...prev,
          {
            value: type.id,
            label: type.type,
            info: type.total_day > 0 ? `Maksimal ${type.total_day} hari` : '',
          },
        ]);
      });
    } else {
      setTypeOptions(typeOptions);
    }
  }, [data]);

  const { data: membersData = [], isLoading: isLoadingMembers } = useQuery({
    queryKey: ['members'],
    queryFn: () => api.get(`/structure/division-members`).then((res) => res.data.data),
  });

  const memberOptions = useMemo(() => {
    const uniqueMap = new Map();

    membersData.forEach((member) => {
      if (member.employe_id !== auth?.employe_id && !uniqueMap.has(member.employe_id)) {
        uniqueMap.set(member.employe_id, {
          value: member.employe_id,
          label: member.employe_name,
        });
      }
    });

    return Array.from(uniqueMap.values());
  }, [membersData]);

  useEffect(() => {
    if (selectedType?.label === 'Sakit') {
      setShowRequestType(true);
    } else {
      setShowRequestType(false);
    }
  }, [selectedType]);

  const handelClose = () => {
    setPopup(false);
    setSelectedType(null);
    document.getElementById('timeOff').reset();
  };

  const validateForm = () => {
    if (isFromManual && requester === null) {
      showToast('Requester harus diisi!', 'error');
      return false;
    }

    if (isFromManual && approver === null) {
      showToast('Approver harus diisi!', 'error');
      return false;
    }

    if (isFromManual && requesterDelegate === null) {
      showToast('Requester Delegate harus diisi!', 'error');
      return false;
    }

    if (selectedType === null) {
      showToast('Jenis cuti harus diisi!', 'error');
      return false;
    }

    if (objectData.date_start === '') {
      showToast('Tanggal mulai cuti harus diisi!', 'error');
      return false;
    }

    if (objectData.date_end === '') {
      showToast('Tanggal akhir cuti harus diisi!', 'error');
      return false;
    }

    if (
      selectedType?.label === 'Cuti Tahunan' &&
      !compareDate(objectData.date_start, objectData.date_end)
    ) {
      return false;
    }

    if (objectData.reason === '') {
      showToast('Alasan cuti harus diisi!', 'error');
      return false;
    }

    if (selectedType?.label === 'Sakit' && objectData.attachment === null) {
      showToast('Attachment harus diisi!', 'error');
      return false;
    }

    if (objectData.attachment !== null) {
      if (isFromManual) {
        if (objectData.attachment?.type !== 'application/pdf') {
          showToast('File harus berekstensi .pdf!', 'error');
          return false;
        }
      } else {
        switch (objectData.attachment?.type) {
          case 'image/png':
            break;
          case 'image/jpg':
            break;
          case 'image/jpeg':
            break;
          default:
            showToast('File harus berekstensi .jpg atau png!', 'error');
            return false;
        }
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValidate = validateForm();

    if (isValidate) {
      setIsSubmitting(true);

      const allData = {
        ...objectData,
        is_manual_form: isFromManual ? 1 : 0,
        requester: requester?.value || null,
        approver: approver?.value || null,
        master_id: selectedType.value,
        delegate_to: isFromManual ? requesterDelegate?.value : selectedMember?.value || null,
      };

      await api
        .post('leave/time-off/store', allData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then(() => {
          handelClose();
          const msg = isFromManual
            ? 'Pengajuan cuti dari form manual berhasil dibuat'
            : 'Pengajuan cuti berhasil dibuat';
          showToast(msg, 'success');
          refetch();
          setIsSubmitting(false);
        })
        .catch((err) => {
          showToast(`Terjadi kesalahan. "${err.response?.data?.message}"`, 'error');
          setIsSubmitting(false);
        });
    }
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: '14px', // rounded pada kontrol utama
      borderColor: state.isFocused ? '#2684FF' : '#ccc',
      boxShadow: state.isFocused ? '0 0 0 1px #2684FF' : 'none',
      '&:hover': {
        borderColor: '#2684FF',
      },
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '10px', // rounded pada dropdown menu
      overflow: 'hidden',
    }),
    menuList: (provided) => ({
      ...provided,
      borderRadius: '10px', // rounded isi list
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#2684FF' : state.isFocused ? '#F0F8FF' : 'white',
      color: state.isSelected ? 'white' : 'black',
      padding: 10,
    }),
  };

  return (
    <Form onSubmit={handleSubmit} className="d-flex flex-column gap-4" id="timeOff">
      {auth?.roles.includes(adminOptionsAllowedRole) && (
        <div className="d-flex flex-column gap-2">
          <div
            className={`d-flex gap-2 border border-${
              isFromManual ? 'primary' : ''
            }  rounded-4 px-3 py-1`}
          >
            <div className="pt-2">
              <Input
                type="checkbox"
                id="frommanual"
                onChange={(e) => setIsFromManual(e.target.checked)}
              />
            </div>
            <div className="pt-2">
              <Label htmlFor="frommanual" className="user-select-none">
                Input Formulir Manual
              </Label>
            </div>
          </div>
          {isFromManual && (
            <FormTimeOffManualAdmin
              {...{
                setObjectData,
                objectData,
                setRequester,
                setApprover,
                setRequesterDelegate,
                customStyles,
              }}
            />
          )}
        </div>
      )}
      <div className="form-group">
        <Label htmlFor="timeOffType">Pilih Jenis Cuti</Label>
        <SelectOption
          loading={isLoadingTypes}
          options={typeOptions}
          onChange={(choice) => setSelectedType(choice)}
          placeHolder="Pilih Jenis Cuti"
        />
      </div>
      {/* <div className="form-group">
        <Label htmlFor="timeOffType">Pilih Jenis Cuti</Label>
        <Select
          id="timeOffType"
          isLoading={isLoadingTypes}
          components={animatedComponents}
          options={typeOptions}
          onChange={(choice) => setSelectedType(choice)}
        />
      </div> */}
      {showRequestType && (
        <div className="form-group">
          <Label htmlFor="requestType">Jenis Pengajuan</Label>
          <select
            className="form-select rounded-4"
            onChange={(e) => setObjectData({ ...objectData, type: e.target.value })}
          >
            <option value="Full Day">Full Day</option>
            <option value="Half day-Before break">Half day-Before break</option>
            <option value="Half day-After break">Half day-After break</option>
          </select>
        </div>
      )}
      <div className="row g-3">
        <div className="col-12 col-md-6">
          <div className="d-flex flex-column form-group">
            <Label htmlFor="startDate">Tanggal Mulai</Label>
            <input
              type="date"
              className="form-control rounded-4"
              id="startDate"
              onChange={(e) => setObjectData({ ...objectData, date_start: e.target.value })}
            />
          </div>
        </div>
        <div className="col-12 col-md-6">
          <div className="d-flex flex-column form-group">
            <Label htmlFor="endDate">Tanggal Berakhir</Label>
            <input
              type="date"
              className="form-control rounded-4"
              id="endDate"
              onChange={(e) => setObjectData({ ...objectData, date_end: e.target.value })}
            />
          </div>
        </div>
      </div>
      <div className="form-group">
        <Label htmlFor="reason">Alasan Cuti</Label>
        <textarea
          className="form-control rounded-4"
          id="reason"
          rows="2"
          onChange={(e) => setObjectData({ ...objectData, reason: e.target.value })}
        ></textarea>
      </div>

      {!isFromManual && (
        <>
          <div className="form-group d-flex flex-column">
            <Label htmlFor="attach">
              Lampiran {selectedType?.label !== 'Sakit' && <small>(Opsional)</small>}
            </Label>
            <input
              type="file"
              className="form-control rounded-4"
              id="attach"
              onChange={(e) => setObjectData({ ...objectData, attachment: e.target.files[0] })}
            />
            <small className="text-primary" style={{ marginTop: '3px', fontSize: '12px' }}>
              File harus berekstensi .jpg atau .png (maks. 20mb)
            </small>
          </div>
          <div className="form-group">
            <Label htmlFor="delegate">Delegasikan ke</Label>
            <Select
              id="delegate"
              styles={customStyles}
              isLoading={isLoadingMembers}
              components={animatedComponents}
              options={memberOptions}
              onChange={(choice) => setSelectedMember(choice)}
            />
          </div>
        </>
      )}
      <div className="d-flex flex-column flex-md-row justify-content-end gap-3">
        {isSubmitting ? (
          <button type="submit" className="btn btn-primary rounded-4" disabled>
            <div className="d-flex gap-2 align-items-center">
              <Spinner size="sm" color="light" />
              Sending..
            </div>
          </button>
        ) : (
          <button type="submit" className="btn btn-primary rounded-4">
            Submit
          </button>
        )}
        <button
          type="button"
          className="btn btn-outline-secondary rounded-4"
          onClick={handelClose}
          disabled={isSubmitting}
        >
          Cancel
        </button>
      </div>
    </Form>
  );
};

FormTimeOff.propTypes = {
  setPopup: PropTypes.func,
  refetch: PropTypes.func,
};

export default FormTimeOff;
